/**
 * Integrytas Group — built-in theme presets.
 *
 * Shipped in code (not the DB) so the platform always has a baseline and a fallback.
 * Org/personal themes (csm_theme) are layered on top by the ThemeProvider. The
 * DEFAULT preset mirrors src/index.css :root exactly, so first paint and the
 * "Default" theme are byte-identical to the historic look.
 */

import type { ThemeDefinition, Palette } from './theme_tokens';

const LIGHT: Palette = {
  'primary': '#00529B', 'primary-hover': '#003F7D', 'primary-soft': '#E6EEF7',
  'surface': '#FFFFFF', 'secondary-bg': '#F3F4F6', 'surface-alt': '#F9FAFB',
  'border': '#94A3B8', 'border-soft': '#CBD5E1', 'border-strong': '#64748B',
  'text': '#1F2937', 'text-muted': '#6B7280', 'text-soft': '#4B5563',
  'sidebar-bg': '#1F2937', 'sidebar-bg-alt': '#111827', 'sidebar-text': '#FFFFFF', 'sidebar-muted': '#9CA3AF',
  'watermark': '#9CA3AF', 'accent': '#374151', 'warn': '#F59E0B',
  'success': '#047857', 'success-soft': '#ECFDF5', 'success-border': '#6EE7B7',
  'warning': '#B45309', 'warning-soft': '#FFFBEB', 'warning-border': '#FCD34D',
  'danger': '#B91C1C', 'danger-soft': '#FEF2F2', 'danger-border': '#FCA5A5',
  'info': '#1D4ED8', 'info-soft': '#EFF6FF', 'info-border': '#93C5FD',
};

const DARK: Palette = {
  ...LIGHT,
  'primary': '#3B82F6', 'primary-hover': '#2563EB', 'primary-soft': '#1E3A5F',
  'surface': '#1F2937', 'secondary-bg': '#111827', 'surface-alt': '#374151',
  'border': '#4B5563', 'border-soft': '#374151', 'border-strong': '#6B7280',
  'text': '#F9FAFB', 'text-muted': '#9CA3AF', 'text-soft': '#D1D5DB',
  'sidebar-bg': '#0F172A', 'sidebar-bg-alt': '#1E293B', 'sidebar-text': '#F8FAFC', 'sidebar-muted': '#94A3B8',
  'watermark': '#6B7280',
  'success-soft': '#064E3B', 'warning-soft': '#451A03', 'danger-soft': '#450A0A', 'info-soft': '#1E3A8A',
};

// Ocean — calm teal/cyan light theme.
const OCEAN: Palette = {
  ...LIGHT,
  'primary': '#0E7490', 'primary-hover': '#155E75', 'primary-soft': '#CFFAFE',
  'secondary-bg': '#ECFEFF', 'surface-alt': '#F0FDFF',
  'border': '#A5C9D2', 'border-soft': '#CDE7EC', 'border-strong': '#6BA3B0',
  'text': '#0F2A33', 'text-soft': '#335A66', 'text-muted': '#5E8390',
  'sidebar-bg': '#0F3D4C', 'sidebar-bg-alt': '#0A2C37', 'sidebar-text': '#FFFFFF', 'sidebar-muted': '#7FB7C4',
  'accent': '#F59E0B', 'watermark': '#7FB7C4',
  'info': '#0E7490', 'info-soft': '#CFFAFE', 'info-border': '#67E8F9',
};

// Oxford & Mayan — the AngelTrace identity (Oxford-blue chrome, Mayan-blue actions,
// Atomic-orange accent), now available platform-wide as a preset.
const OXFORD: Palette = {
  ...LIGHT,
  'primary': '#33CCFF', 'primary-hover': '#1FBFF2', 'primary-soft': '#E6F9FF',
  'secondary-bg': '#F0F2F5', 'surface-alt': '#F8FAFC',
  'border': '#D6DBE2', 'border-soft': '#E5E9EF', 'border-strong': '#7A8799',
  'text': '#0C1B3B', 'text-soft': '#33485F', 'text-muted': '#7A8799',
  'sidebar-bg': '#0C1B3B', 'sidebar-bg-alt': '#081230', 'sidebar-text': '#FFFFFF', 'sidebar-muted': '#7A8799',
  'accent': '#FF9326', 'watermark': '#7A8799',
  'info': '#33CCFF', 'info-soft': '#E6F9FF', 'info-border': '#9BE3FF',
};

export const BUILTIN_THEMES: ThemeDefinition[] = [
  { code: 'DEFAULT', name: 'Slate & Steel (default)', scope: 'builtin', mode: 'light',
    colors: LIGHT, font: "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    density: 'compact', fontSize: 'medium' },
  { code: 'DARK', name: 'Midnight (dark)', scope: 'builtin', mode: 'dark',
    colors: DARK, font: "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    density: 'compact', fontSize: 'medium' },
  { code: 'OCEAN', name: 'Ocean', scope: 'builtin', mode: 'light',
    colors: OCEAN, font: "'Inter', system-ui, sans-serif",
    density: 'compact', fontSize: 'medium' },
  { code: 'OXFORD', name: 'Oxford & Mayan', scope: 'builtin', mode: 'light',
    colors: OXFORD, font: "'Roboto', system-ui, sans-serif",
    density: 'compact', fontSize: 'medium' },
];

export const DEFAULT_THEME = BUILTIN_THEMES[0];
export const DEFAULT_PALETTE = LIGHT;
